import { useEffect, useRef, useState } from 'react';

const SEGMENTS = 14;
// Segment thresholds (as fractions of 1.0). Below threshold[i] the segment is lit.
const THRESHOLDS = Array.from({ length: SEGMENTS }, (_, i) => (i + 1) / SEGMENTS);
// Color per segment: bottom green → yellow → red at the top
const colorForSegment = (i) => {
  if (i >= SEGMENTS - 2) return '#ef4444'; // top 2 → red
  if (i >= SEGMENTS - 5) return '#eab308'; // next 3 → yellow
  return '#84cc16'; // rest → green (lime)
};

/**
 * Horizontal LED-style level meter with peak-hold.
 *
 * Two modes driven by the engine:
 *  - RECORDING → shows live mic level of the current take
 *  - AMBIENT   → shows live room noise between takes so the artist can watch
 *                the level drop as they close a window/fridge before hitting
 *                Record.
 * Both modes read via engine.getMicLevel(); we just relabel + gate rendering.
 */
export function LevelMeter({ engine }) {
  const { isRecording, ambientMonitorActive, getMicLevel, getMicDb } = engine;
  const active = isRecording || ambientMonitorActive;
  const [level, setLevel] = useState(0);
  const [peakHold, setPeakHold] = useState(0);
  const [db, setDb] = useState(null);
  const rafRef = useRef(null);
  const peakRef = useRef({ value: 0, decayFrom: 0 });
  const dbRef = useRef({ smoothed: null, lastUpdate: 0 });

  useEffect(() => {
    if (!active) {
      cancelAnimationFrame(rafRef.current);
      setLevel(0);
      setPeakHold(0);
      setDb(null);
      peakRef.current = { value: 0, decayFrom: 0 };
      dbRef.current = { smoothed: null, lastUpdate: 0 };
      return undefined;
    }

    const tick = () => {
      const l = getMicLevel();
      setLevel(l);
      const now = performance.now();
      const p = peakRef.current;
      if (l >= p.value) {
        // new peak — hold it
        peakRef.current = { value: l, decayFrom: now };
      } else if (now - p.decayFrom > 800) {
        // decay after 800ms hold
        const decayed = Math.max(l, p.value - 0.02);
        peakRef.current = { value: decayed, decayFrom: p.decayFrom };
      }
      setPeakHold(peakRef.current.value);
      // Live dB readout — lightly smoothed, repainted ~4x/sec so it's readable
      const d = getMicDb ? getMicDb() : null;
      if (d != null) {
        const s = dbRef.current.smoothed == null ? d : dbRef.current.smoothed * 0.8 + d * 0.2;
        dbRef.current.smoothed = s;
        if (now - dbRef.current.lastUpdate > 250) {
          dbRef.current.lastUpdate = now;
          setDb(s);
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [active, getMicLevel, getMicDb]);

  const isClipping = isRecording && level >= 0.98;
  const isSilent = isRecording && level < 0.02;
  const label = isRecording ? 'MIC' : 'ROOM';
  const title = isRecording
    ? 'Mic input level'
    : ambientMonitorActive
      ? 'Live room noise — watch it drop as you quiet the room before recording'
      : 'Level meter (active once mic is granted)';

  return (
    <div
      className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-[#0a0a0a] border border-[#222]"
      data-testid="mic-level-meter"
      data-meter-mode={isRecording ? 'mic' : ambientMonitorActive ? 'room' : 'idle'}
      title={title}
    >
      <span
        className={`text-[9px] font-bold tracking-wide uppercase ${isRecording ? 'text-red-400' : 'text-[#666]'}`}
        data-testid="mic-level-mode-label"
      >
        {label}
      </span>
      <div className="flex gap-[2px] items-center">
        {THRESHOLDS.map((threshold, i) => {
          const lit = active && level >= threshold - 1 / SEGMENTS / 2;
          const peakLit = active && !lit && peakHold >= threshold - 1 / SEGMENTS / 2 && peakHold < threshold + 1 / SEGMENTS / 2;
          const color = colorForSegment(i);
          return (
            <span
              key={i}
              className="rounded-[1px] transition-opacity"
              style={{
                width: 4,
                height: 14,
                backgroundColor: lit ? color : peakLit ? color : '#1a1a1a',
                opacity: lit ? 1 : peakLit ? 0.6 : 1,
                boxShadow: lit ? `0 0 4px ${color}80` : 'none',
              }}
              data-testid={`mic-level-seg-${i}`}
            />
          );
        })}
      </div>
      {active && !isRecording && db != null && (
        <span
          className={`text-[9px] font-mono w-12 text-right tabular-nums ${db >= -30 ? 'text-red-400' : db >= -45 ? 'text-yellow-500' : 'text-lime'}`}
          title="Live room noise floor — green below -45 dB, amber to -30 dB, red above"
          data-testid="room-db-label"
        >
          {Math.max(-99, Math.round(db))} dB
        </span>
      )}
      {active && isRecording && (
        <span
          className={`text-[9px] font-mono w-8 text-right ${isClipping ? 'text-red-400' : isSilent ? 'text-yellow-500' : 'text-[#888]'}`}
          data-testid="mic-level-label"
        >
          {isClipping ? 'CLIP' : isSilent ? 'LOW' : `${Math.round(level * 100)}%`}
        </span>
      )}
    </div>
  );
}
